import { useCallback, useEffect, useRef, useState } from 'react';
import { get, set, del, update } from 'idb-keyval';
import InAppCamera from './InAppCamera';
import { analyzeImage } from '../services/mlService';
import {
  auswerten, kurzNr, tpt, istUnscharf,
  isTurboHudOn, setTurboHudOn,
  isTurboAutoFotoOn, setTurboAutoFotoOn,
  isTurboRegalOn, setTurboRegalOn,
  isTurboTonOn, setTurboTonOn,
} from '../services/turboPaketPilot';
import { printOne, isPrinterSupported, isAutoPrintEnabled, getPrinterConfig } from '../services/printerService';
import { addToBatch } from '../services/barcodeBatch';
import { hasCustomFeature } from '../services/customFeatures';
import { dpdAktiv, dpdAnnahmeAktiv, dpdAnnahme, dpdCodeAusTracking, vorladenStart as dpdVorladenStart } from '../services/dpd';

/**
 * ⚡ Turbo PaketPilot — Kamera bleibt offen, jedes Foto läuft im Hintergrund durch (services/turboPaketPilot.js).
 *
 * Reihenfolge: Erkennung bis zu 3 gleichzeitig, Anlegen + Etikett streng in Foto-Reihenfolge über eine Kette — sonst
 * passt das Etikett nicht mehr zum Paket im Stapel. Fotos liegen bis zum Anlegen im Gerätespeicher (IndexedDB) und
 * werden nach einem Neustart beim nächsten Öffnen nachgeholt.
 *
 * Anzeige (Wunsch Philipp 23.10.2026): Leiste „19/20 ✓ · Ø 3,0 s · 2 in Arbeit", Fotostreifen (jedes Foto eine Kachel
 * mit Nummer: grün = gespeichert mit Fach, rot „?" = bitte prüfen, orange = schon erfasst), das letzte Fach groß, eine
 * Regalkarte A–Z, deren Fach beim Speichern aufleuchtet (je öfter heute, desto heller), dazu Töne und Vibration.
 */
const OFFEN_KEY = 'turbo_pp_offen';
const fotoKey = (id) => `turbo_pp_foto_${id}`;
const MAX_PARALLEL = 5;
// Fotos, die gerade in Arbeit sind — über das Schließen/Öffnen hinweg, damit nichts doppelt angelegt wird
const inArbeit = new Set();
const FAECHER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

let audioCtx = null;
function ton(freq, ms, type = 'sine', verzoegerung = 0) {
  if (!isTurboTonOn()) return;
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    const t0 = audioCtx.currentTime + verzoegerung / 1000;
    osc.type = type; osc.frequency.value = freq; gain.gain.value = 0.18;
    osc.connect(gain); gain.connect(audioCtx.destination);
    osc.start(t0); osc.stop(t0 + ms / 1000);
  } catch { /* kein Ton möglich */ }
}
const vibrieren = (muster) => { try { if (navigator.vibrate) navigator.vibrate(muster); } catch { /* */ } };
// Rückmeldung zum Hören und Spüren: Auslösen kurz, gespeichert = aufsteigender Zweiklang, „?" = tief doppelt, doppelt = drei kurze
const RUECKMELDUNG = {
  ausloesen: () => { ton(1200, 35); vibrieren(15); },
  fertig: () => { ton(784, 90); ton(1175, 140, 'sine', 90); vibrieren(40); },
  frage: () => { ton(300, 200, 'square'); ton(300, 200, 'square', 280); vibrieren([200, 100, 200]); },
  doppelt: () => { [0, 140, 280].forEach((v) => ton(520, 80, 'triangle', v)); vibrieren([70, 60, 70, 60, 70]); },
};

// 👁 Vorschau ohne Server/Speichern/Druck (?turbodemo=1 bzw. Knopf in den Experimentierfunktionen): erfundene Pakete
const DEMO_NAMEN = ['Sanne de Jong', 'Daan Visser', 'Emma Bakker', 'Lucas Smit', 'Julia Meijer', 'Noah de Boer', 'Tess Mulder', 'Sem Bos',
  'Lotte Vos', 'Finn Peters', 'Mila Hendriks', 'Levi van Dijk', 'Sophie Dekker', 'Bram Janssen', 'Fenna Kok', 'Ruben van Leeuwen'];
function demoErgebnis(n) {
  const name = DEMO_NAMEN[(n * 7) % DEMO_NAMEN.length];
  const fach = name.split(' ').pop().charAt(0).toUpperCase();
  const r = Math.random();
  if (n > 2 && r < 0.08) return { stand: 'doppelt', name, fach: '', grund: 'doppelt' };
  if (r < 0.2) return { stand: 'frage', name: '?', fach: '?', grund: ['keinName', 'unscharf', 'nameUnsicher'][n % 3] };
  return { stand: 'fertig', name, fach, grund: '' };
}
function demoThumb(n) {
  const farben = ['#8d6e63', '#78909c', '#a1887f', '#6d4c41', '#546e7a'];
  const bg = farben[n % farben.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" rx="14" fill="${bg}"/><rect x="20" y="16" width="80" height="88" rx="6" fill="#fafafa"/><rect x="26" y="22" width="42" height="8" rx="2" fill="#ffcc00"/><rect x="26" y="36" width="64" height="6" rx="2" fill="#263238"/><rect x="26" y="46" width="50" height="5" rx="2" fill="#546e7a"/><rect x="26" y="62" width="68" height="18" fill="#212121"/><text x="60" y="96" font-family="sans-serif" font-size="13" font-weight="bold" text-anchor="middle" fill="#37474f">#${n}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
async function dateiZuThumb(datei) {
  try {
    const bmp = await createImageBitmap(datei);
    try {
      const sc = Math.min(1, 360 / Math.max(bmp.width, bmp.height));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(bmp.width * sc));
      c.height = Math.max(1, Math.round(bmp.height * sc));
      c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', 0.72);
    } finally { try { bmp.close(); } catch { /* */ } }
  } catch { return null; }
}

const neueId = () => (window.crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`);
const FARBE = { fertig: '#00e676', frage: '#ff1744', doppelt: '#ff9100', erkennt: '#4dd0e1', wartet: '#ffd740' };

const CSS = `
@keyframes tpEin { from { transform: translateY(-14px) scale(0.6); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes tpLaden { 0% { opacity: .35; } 50% { opacity: 1; } 100% { opacity: .35; } }
@keyframes tpPuls { 0%,100% { box-shadow: 0 0 0 0 rgba(255,23,68,.7); } 50% { box-shadow: 0 0 0 6px rgba(255,23,68,0); } }
@keyframes tpLeucht { 0% { transform: scale(1.35); box-shadow: 0 0 18px 6px rgba(0,230,118,.9); } 100% { transform: none; box-shadow: 0 0 0 0 rgba(0,230,118,0); } }
@keyframes tpGross { from { transform: scale(1.25); opacity: .2; } to { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) { [data-tp-anim] { animation: none !important; } }
`;

export default function TurboPaketPilot({ language, t, holePakete, onAnlegen, onBearbeiten, onClose, demo = false }) {
  const T = tpt(language);
  const [eintraege, setEintraege] = useState([]); // [{id, nr, stand, name, fach, grund, paket, t0, dauer, thumb}]
  const [phase, setPhase] = useState('kamera'); // kamera | zusammenfassung
  const [fortsetzen, setFortsetzen] = useState(0);
  const [leuchtet, setLeuchtet] = useState(null); // { fach, n } — Regalkarte: zuletzt gespeichertes Fach aufleuchten lassen
  const [zoomThumb, setZoomThumb] = useState(null); // { nr, name, thumb } — Vollbild-Vorschau beim Antippen des Mini-Fotos
  const [hudAn, setHudAn] = useState(() => isTurboHudOn());
  const [autoFotoAn, setAutoFotoAn] = useState(() => isTurboAutoFotoOn());
  const [regalAn, setRegalAn] = useState(() => isTurboRegalOn());
  const [tonAn, setTonAn] = useState(() => isTurboTonOn());
  const [zeigeOpt, setZeigeOpt] = useState(false);
  const kette = useRef(Promise.resolve());
  const nr = useRef(0);
  const slots = useRef({ belegt: 0, wartende: [] });
  const lebt = useRef(true);
  useEffect(() => {
    if (dpdAktiv()) dpdVorladenStart(false).catch(() => {});
    return () => { lebt.current = false; };
  }, []);

  const setze = useCallback((id, felder) => {
    if (!lebt.current) return;
    setEintraege((alt) => (alt.some((e) => e.id === id) ? alt.map((e) => (e.id === id ? { ...e, ...felder } : e)) : [...alt, { id, ...felder }]));
  }, []);

  // Erkennung mit höchstens MAX_PARALLEL gleichzeitigen Anfragen
  const mitSlot = useCallback((fn) => new Promise((resolve) => {
    const s = slots.current;
    const start = () => {
      s.belegt++;
      fn().then(resolve, (e) => resolve({ error: (e && e.message) || 'Fehler' })).finally(() => {
        s.belegt--;
        const naechster = s.wartende.shift();
        if (naechster) naechster();
      });
    };
    if (s.belegt < MAX_PARALLEL) start(); else s.wartende.push(start);
  }), []);

  const vergiss = useCallback(async (id) => {
    inArbeit.delete(id);
    try { await del(fotoKey(id)); await update(OFFEN_KEY, (l) => (l || []).filter((x) => x !== id)); } catch { /* */ }
  }, []);

  const drucken = (paket, zusatz) => {
    if (!isPrinterSupported() || !isAutoPrintEnabled() || !getPrinterConfig()?.address) return;
    printOne(paket, { zusatz }).catch(() => { /* printOne meldet ins Fehlerprotokoll */ });
  };

  // Ein Foto: Erkennung sofort (parallel), Anlegen + Etikett in der Kette (Foto-Reihenfolge)
  const einreihen = useCallback((id, datei, schaerfe = null, thumb = null, istAuto = false) => {
    inArbeit.add(id);
    const n = ++nr.current;
    const t0 = Date.now();
    const vorschaubild = thumb || (demo ? demoThumb(n) : null);
    setze(id, { nr: n, stand: 'erkennt', t0, thumb: vorschaubild });
    if (demo) {
      const verz = new Promise((res) => setTimeout(res, 800 + Math.random() * 2400));
      kette.current = kette.current.then(async () => {
        await verz;
        const d = demoErgebnis(n);
        RUECKMELDUNG[d.stand]();
        if (d.stand !== 'doppelt' && lebt.current) setLeuchtet({ fach: d.fach, n });
        setze(id, { ...d, dauer: Date.now() - t0 });
      });
      return;
    }
    const erkennung = mitSlot(() => analyzeImage(datei, { onWarte: () => setze(id, { stand: 'wartet' }) }));
    kette.current = kette.current.then(async () => {
      const r = await erkennung;
      const pakete = holePakete() || [];
      const a = auswerten(r, pakete);
      // Unscharfes Foto als Grund nennen, wenn die Erkennung deshalb nichts Brauchbares fand
      if (a.unsicher && (a.grund === 'keinName' || a.grund === 'erkennung') && istUnscharf(schaerfe)) a.grund = 'unscharf';
      // Bei Auto-Foto ohne erkannten Namen: Fehlauslösung verwerfen, kein „?"-Paket anlegen und kein Etikett drucken
      if (istAuto && !a.felder.name) {
        if (lebt.current) setEintraege((alt) => alt.filter((e) => e.id !== id));
        await vergiss(id);
        return;
      }
      if (a.doppelt) {
        RUECKMELDUNG.doppelt();
        setze(id, { stand: 'doppelt', name: a.felder.name, fach: '', grund: 'doppelt', dauer: Date.now() - t0 });
        await vergiss(id);
        return;
      }
      const name = a.felder.name || '?';
      const fach = a.unsicher ? '?' : a.fach;
      const paket = await onAnlegen({ ...a.felder, name, regalplatz: fach });
      if (dpdAnnahmeAktiv() && !/^(vinted|dhl|mondial|inpost|gls|ups|postnl|bpost|hermes)/i.test(a.felder.paketdienst || '')) {
        const dCode = dpdCodeAusTracking(a.felder.trackingNumber);
        if (dCode && (/^dpd$/i.test(a.felder.paketdienst || '') || String(a.felder.trackingNumber || '').replace(/\D/g, '').length >= 27)) {
          dpdAnnahme(dCode).catch(() => {});
        }
      }
      await vergiss(id); // angelegt → nach einem Neustart nicht noch einmal
      const k = kurzNr(a.felder.trackingNumber);
      drucken(paket, [a.unsicher ? '?' : '', k ? `#${k}` : ''].filter(Boolean).join(' '));
      // 📋 Barcode-Stapel wie beim normalen Erfassen (Barcodes liest der Server mit)
      if (hasCustomFeature('batchBarcodes') && (Array.isArray(r && r.barcodes) || a.felder.trackingNumber)) {
        const plz = (r && r.ocrResult && r.ocrResult.postal) || '';
        addToBatch({ name, regalplatz: fach, tracking: a.felder.trackingNumber, plz, manuellBestaetigt: false, imageId: a.felder.imageUrl || null, dienst: a.felder.paketdienst || '', codes: (r && r.barcodes) || [] });
      }
      if (a.unsicher) RUECKMELDUNG.frage(); else RUECKMELDUNG.fertig();
      if (lebt.current) setLeuchtet({ fach: a.unsicher ? '?' : String(fach).charAt(0).toUpperCase(), n });
      setze(id, { stand: a.unsicher ? 'frage' : 'fertig', name, fach, grund: a.grund, hinweis: a.hinweis, paket, dauer: Date.now() - t0 });
    }).catch((e) => {
      console.warn('⚡ Turbo PaketPilot:', e && e.message);
      vergiss(id);
      if (istAuto) {
        if (lebt.current) setEintraege((alt) => alt.filter((e) => e.id !== id));
        return;
      }
      RUECKMELDUNG.frage();
      setze(id, { stand: 'frage', name: '?', fach: '?', grund: 'erkennung', dauer: Date.now() - t0 });
    });
  }, [demo, holePakete, mitSlot, onAnlegen, setze, vergiss]);

  // Fotos vom letzten Mal (App vorher geschlossen/abgestürzt) nachholen — einmal je Öffnen
  const nachgeholt = useRef(false);
  useEffect(() => {
    if (nachgeholt.current || demo) return;
    nachgeholt.current = true;
    (async () => {
      let offen = [];
      try { offen = (await get(OFFEN_KEY)) || []; } catch { offen = []; }
      const nachholen = offen.filter((id) => !inArbeit.has(id));
      if (!nachholen.length) return;
      setFortsetzen(nachholen.length);
      for (const id of nachholen) {
        let datei = null;
        try { datei = await get(fotoKey(id)); } catch { datei = null; }
        if (datei) {
          const thumb = await dateiZuThumb(datei);
          einreihen(id, datei, null, thumb, false);
        } else await vergiss(id);
      }
    })();
  }, [demo, einreihen, vergiss]);

  // Ton/Vibration kommen schon beim Auslösen (onAusgeloest der Kamera), nicht erst nach dem JPEG-Kodieren
  const aufnehmen = (datei, info = {}) => {
    const id = neueId();
    if (demo) { einreihen(id, null, null, null, !!info.auto); return; }
    einreihen(id, datei, info.schaerfe, info.thumb, !!info.auto);
    // Im Gerätespeicher sichern, ohne die Erkennung aufzuhalten. Ist das Foto schon fertig (z. B. sofort als doppelt
    // erkannt), bevor die Sicherung durch ist, NICHT nachträglich in die offene Liste eintragen (Review 23.10.2026)
    set(fotoKey(id), datei)
      .then(() => (inArbeit.has(id) ? update(OFFEN_KEY, (l) => (inArbeit.has(id) ? [...(l || []), id] : (l || []))) : del(fotoKey(id))))
      .catch(() => { /* ohne Sicherung weiter */ });
  };

  // Demo spielt sich selbst ab: 14 Fotos im Takt von ~1,4 s (der Auslöser geht zusätzlich)
  const aufnehmenRef = useRef(aufnehmen); aufnehmenRef.current = aufnehmen;
  useEffect(() => {
    if (!demo) return undefined;
    let k = 0;
    const iv = setInterval(() => { if (++k > 14) { clearInterval(iv); return; } aufnehmenRef.current(null, {}); }, 1400);
    return () => clearInterval(iv);
  }, [demo]);

  // ── Kennzahlen ──
  const offen = eintraege.filter((e) => e.stand === 'erkennt' || e.stand === 'wartet').length;
  const erledigt = eintraege.filter((e) => e.stand === 'fertig' || e.stand === 'frage' || e.stand === 'doppelt');
  const gespeichert = eintraege.filter((e) => e.stand === 'fertig');
  const fragen = eintraege.filter((e) => e.stand === 'frage');
  const doppelte = eintraege.filter((e) => e.stand === 'doppelt');
  const mitZeit = erledigt.filter((e) => Number.isFinite(e.dauer));
  const schnitt = mitZeit.length ? mitZeit.reduce((s, e) => s + e.dauer, 0) / mitZeit.length / 1000 : null;
  const letzter = [...eintraege].reverse().find((e) => e.stand === 'fertig' || e.stand === 'frage' || e.stand === 'doppelt');
  // Regalkarte: wie oft heute in dieses Fach (diese Runde)
  const jeFach = {};
  for (const e of gespeichert) { const b = String(e.fach || '').charAt(0).toUpperCase(); if (b) jeFach[b] = (jeFach[b] || 0) + 1; }
  const maxFach = Math.max(1, ...Object.values(jeFach));

  const beenden = () => {
    if (offen > 0 && !window.confirm(T('offen', { n: offen }))) return;
    if (fragen.length || doppelte.length) setPhase('zusammenfassung'); else onClose();
  };

  if (phase === 'zusammenfassung') {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 8100, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div data-testid="tp-zusammenfassung" style={{ background: '#23272f', color: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', padding: 18 }}>
          <b style={{ fontSize: 18 }}>{T('zusammenfassung')}</b>
          <div style={{ marginTop: 6, fontSize: 14, color: '#bbb' }}>{T('erfolgreich', { a: gespeichert.length, b: erledigt.length })}{schnitt != null ? ` · ${T('schnitt', { s: schnitt.toFixed(1) })}` : ''}{doppelte.length ? ` · ${T('doppelt', { n: doppelte.length })}` : ''}</div>
          {fragen.length > 0 && <div style={{ marginTop: 14, fontWeight: 700 }}>{T('pruefen')}</div>}
          {fragen.map((e) => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #3a3f48' }}>
              <span style={{ color: '#aaa', minWidth: 22, fontWeight: 600 }}>{e.nr}</span>
              {e.thumb && (
                <img
                  src={e.thumb}
                  alt={`#${e.nr}`}
                  data-testid={`tp-thumb-${e.nr}`}
                  onClick={() => setZoomThumb({ nr: e.nr, name: e.name, thumb: e.thumb })}
                  style={{ width: 40, height: 40, borderRadius: 7, objectFit: 'cover', border: '1.5px solid #ff5252', cursor: 'pointer', flexShrink: 0, background: '#111' }}
                />
              )}
              <span style={{ flex: 1, minWidth: 0 }}>{e.name} <span style={{ color: '#ff8a80' }}>· {T(`g_${e.grund}`)}</span></span>
              {e.paket && <button type="button" data-testid={`tp-bearbeiten-${e.nr}`} onClick={() => onBearbeiten(e.paket)}
                style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>{T('bearbeiten')}</button>}
            </div>
          ))}
          {doppelte.map((e) => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #3a3f48', color: '#bbb' }}>
              {e.thumb && (
                <img
                  src={e.thumb}
                  alt={`#${e.nr}`}
                  data-testid={`tp-thumb-${e.nr}`}
                  onClick={() => setZoomThumb({ nr: e.nr, name: e.name, thumb: e.thumb })}
                  style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', border: '1px solid #ff9100', cursor: 'pointer', flexShrink: 0, background: '#111' }}
                />
              )}
              <span>{e.nr} · {e.name || '?'} · {T('g_doppelt')}</span>
            </div>
          ))}
          <button type="button" data-testid="tp-schliessen" onClick={onClose}
            style={{ width: '100%', marginTop: 16, padding: 12, borderRadius: 10, border: 'none', background: '#444', color: '#fff', fontSize: 15, cursor: 'pointer' }}>{T('schliessen')}</button>
        </div>
        {zoomThumb && (
          <div
            data-testid="tp-thumb-zoom"
            onClick={() => setZoomThumb(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 8200, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'pointer' }}
          >
            <img src={zoomThumb.thumb} alt={`#${zoomThumb.nr}`} style={{ maxWidth: '88vw', maxHeight: '72vh', borderRadius: 12, border: '2px solid #fff', objectFit: 'contain', background: '#111' }} />
            <div style={{ color: '#fff', marginTop: 12, fontSize: 16, fontWeight: 700 }}>#{zoomThumb.nr} · {zoomThumb.name || '?'}</div>
          </div>
        )}
      </div>
    );
  }

  // ── Oben: Statusleiste + anpassbare Schnell-Optionen (⚙️) ──
  const schalteHud = (next) => {
    setHudAn(next);
    setTurboHudOn(next);
    if (!next) setAutoFotoAn(false);
  };
  const schalteAutoFoto = (next) => {
    setAutoFotoAn(next);
    setTurboAutoFotoOn(next);
    if (next) setHudAn(true);
  };
  const schalteRegal = (next) => { setRegalAn(next); setTurboRegalOn(next); };
  const schalteTon = (next) => { setTonAn(next); setTurboTonOn(next); };
  const optKnopf = (id, aktiv, label, onClick) => (
    <button
      key={id}
      type="button"
      data-testid={id}
      aria-pressed={aktiv}
      onClick={onClick}
      style={{
        padding: '5px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
        border: `1.5px solid ${aktiv ? '#00e676' : 'rgba(255,255,255,0.22)'}`,
        background: aktiv ? 'rgba(0,230,118,0.18)' : 'rgba(255,255,255,0.06)',
        color: aktiv ? '#00e676' : '#90a4ae',
      }}
    >
      {label}
    </button>
  );
  const leiste = (
    <div data-testid="turbo-pp" style={{ width: '100%', padding: '8px 12px', boxSizing: 'border-box', color: '#e0f7fa', fontFamily: 'system-ui, sans-serif',
      background: 'linear-gradient(180deg, rgba(0,229,255,.14), rgba(0,0,0,0))', borderBottom: '1px solid rgba(0,229,255,.35)' }}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, fontSize: 12.5, fontWeight: 700, letterSpacing: '.02em' }}>
        <span style={{ color: '#ffd740', textShadow: '0 0 8px rgba(255,215,64,.6)', whiteSpace: 'nowrap' }}>{T('badge')}{demo ? <span data-testid="tp-demo" style={{ color: '#ff80ab' }}> · DEMO</span> : null}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span data-testid="tp-zaehler" style={{ display: 'flex', gap: 8, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <span style={{ color: FARBE.fertig }}>{T('erfolgreich', { a: gespeichert.length, b: erledigt.length })}</span>
            {schnitt != null && <span data-testid="tp-schnitt">{T('schnitt', { s: schnitt.toFixed(1) })}</span>}
            <span data-testid="tp-warteschlange" style={{ color: offen ? FARBE.erkennt : '#78909c' }}>{T('inArbeit', { n: offen })}</span>
          </span>
          <button
            type="button"
            data-testid="tp-opt-toggle"
            aria-expanded={zeigeOpt}
            onClick={() => setZeigeOpt((v) => !v)}
            style={{
              background: zeigeOpt ? 'rgba(0,229,255,0.22)' : 'rgba(255,255,255,0.1)',
              border: `1px solid ${zeigeOpt ? '#00e5ff' : 'rgba(255,255,255,0.2)'}`,
              color: '#fff', borderRadius: 8, padding: '3px 7px', fontSize: 13, cursor: 'pointer', lineHeight: 1.1,
            }}
          >
            ⚙️
          </button>
        </span>
      </div>
      {zeigeOpt && (
        <div data-testid="tp-opt-panel" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          {optKnopf('tp-opt-hud', hudAn, T('opt_hud'), () => schalteHud(!hudAn))}
          {optKnopf('tp-opt-auto', autoFotoAn, T('opt_auto'), () => schalteAutoFoto(!autoFotoAn))}
          {optKnopf('tp-opt-regal', regalAn, T('opt_regal'), () => schalteRegal(!regalAn))}
          {optKnopf('tp-opt-ton', tonAn, T('opt_ton'), () => schalteTon(!tonAn))}
        </div>
      )}
      {fortsetzen > 0 && <div style={{ fontSize: 12, color: '#ffcc80', marginTop: 2 }}>{T('fortsetzen', { n: fortsetzen })}</div>}
    </div>
  );

  // ── Unten: Fotostreifen, letztes Ergebnis, Regalkarte ──
  const kachel = (e) => {
    const laeuft = e.stand === 'erkennt' || e.stand === 'wartet';
    const f = FARBE[e.stand] || '#888';
    return (
      <div key={e.id} data-testid={`tp-eintrag-${e.nr}`} data-stand={e.stand} data-tp-anim title={e.name || ''}
        style={{ flex: '0 0 auto', width: 44, height: 50, borderRadius: 8, border: `2px solid ${f}`, background: `${f}22`, color: '#fff',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.05,
          animation: `tpEin .25s ease-out${laeuft ? ', tpLaden 1s ease-in-out infinite' : ''}${e.stand === 'frage' ? ', tpPuls 1.2s ease-out infinite' : ''}` }}>
        <span style={{ fontSize: 10, opacity: 0.75 }}>{e.nr}</span>
        <b style={{ fontSize: 18, color: f }}>{laeuft ? '⏳' : e.stand === 'doppelt' ? '⧉' : e.stand === 'frage' ? '?' : (e.fach || '✓')}</b>
      </div>
    );
  };
  const unten = (
    <div style={{ width: '100%', padding: '10px 12px 16px', boxSizing: 'border-box', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
      {eintraege.length > 0 && (
        <div data-testid="tp-streifen" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {[...eintraege].reverse().map(kachel)}
        </div>
      )}
      <div data-testid="tp-letzter" style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, minHeight: 58 }}>
        {letzter ? (<>
          <span key={`g${letzter.id}`} data-tp-anim style={{ fontSize: 50, fontWeight: 800, lineHeight: 1, minWidth: 56, textAlign: 'center',
            color: FARBE[letzter.stand], textShadow: `0 0 16px ${FARBE[letzter.stand]}99`, animation: 'tpGross .3s ease-out' }}>
            {letzter.stand === 'doppelt' ? '⧉' : letzter.fach || '—'}
          </span>
          {letzter.thumb && (
            <img
              src={letzter.thumb}
              alt={`#${letzter.nr}`}
              data-testid="tp-letzter-thumb"
              style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover', border: `1.5px solid ${FARBE[letzter.stand]}`, flexShrink: 0, background: '#111' }}
            />
          )}
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12, color: '#90a4ae' }}>#{letzter.nr}{Number.isFinite(letzter.dauer) ? ` · ${(letzter.dauer / 1000).toFixed(1)} s` : ''}</span>
            <span style={{ display: 'block', fontSize: 17, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{letzter.name}</span>
            {letzter.grund ? <span style={{ display: 'block', color: '#ff8a80', fontSize: 13 }}>{T(`g_${letzter.grund}`)}</span> : null}
            {!letzter.grund && letzter.hinweis ? <span data-testid="tp-hinweis" style={{ display: 'block', color: '#ffd740', fontSize: 13 }}>{T(`h_${letzter.hinweis}`)}</span> : null}
          </span>
        </>) : <span style={{ fontSize: 15, color: '#90a4ae' }}>{T('bereit')}</span>}
      </div>
      {regalAn && (
        <div data-testid="tp-regal" style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginTop: 10 }}>
          {[...FAECHER, '?'].map((b) => {
            const n = b === '?' ? fragen.length : (jeFach[b] || 0);
            const an = leuchtet && leuchtet.fach === b;
            const grund = b === '?' ? FARBE.frage : FARBE.fertig;
            return (
              <div key={an ? `${b}-${leuchtet.n}` : b} data-testid={`tp-fach-${b}`} data-anzahl={n} data-tp-anim
                style={{ position: 'relative', height: 30, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                  color: n ? '#fff' : '#546e7a', border: `1px solid ${n ? grund : '#263238'}`,
                  background: n ? `${grund}${Math.round(25 + 140 * (b === '?' ? 1 : n / maxFach)).toString(16).padStart(2, '0')}` : 'rgba(255,255,255,.03)',
                  animation: an ? 'tpLeucht .9s ease-out' : 'none' }}>
                {b}
                {n > 0 && <span style={{ position: 'absolute', top: 1, right: 3, fontSize: 8, opacity: 0.8 }}>{n}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <InAppCamera
      t={t}
      hudTexte={T}
      hudAn={hudAn}
      autoFotoAn={autoFotoAn}
      onToggleAutoFoto={schalteAutoFoto}
      videoMaxHeight={regalAn ? '46vh' : '60vh'}
      bleibtOffen
      demo={demo}
      cancelLabel={T('beenden')}
      onClose={beenden}
      onCapture={aufnehmen}
      onAusgeloest={RUECKMELDUNG.ausloesen}
      unten={unten}
    >
      {leiste}
    </InAppCamera>
  );
}
